import React, { useState, useEffect } from 'react';
import { useAppConstantsContext } from '@/context/AppConstants';
import { RecentReportSerialized } from '@/recentReports/index';
import { serializedTypes, useRewardsReport } from '@/reports/index';
import { SerializeProperty } from '@/reports/property/propertyReportSerializer';
import { Stack, Text, Modal } from '@/theme/components';
import { styled, Icons } from '@/theme/index';
import { useScreenSize } from '@/utils/useScreenSize';
import SearchPropertyContent from './SearchPropertyContent';
import ClickOutPartner from '../ClickOutPartner/index';
import { CarwiserModal } from '../rewards/Carwiser/CarwiserModal';
import { Rewards } from '../rewards/Rewards';
import { useRewards } from '../rewards/helpers';
import { Reward } from '../rewards/types';

const RewardsBanner = styled('img')(() => ({
  width: '100%',
}));

const TextWithIcon = styled(Text)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: 0,

  svg: {
    marginRight: theme.spacing(1),
  },
}));

const modalStyles = {
  width: '90vw',
  margin: 'auto',
  '.MuiDialog-paper': {
    maxWidth: ['90vw', '90vw', '85vw', '1066px'],
    width: ['90vw', '90vw', '85vw', '1066px'],
    margin: 'auto',
  },
};

const CLICK_OUT_PARTNER_COMPONENT = 'rewards/clickout-partner/widget';

interface PerksPageProps {
  filterByCategories?: boolean;
  title?: string;
  useSearch?: boolean;
  last5RecentReports?: RecentReportSerialized[];
  isRecentReportsLoading?: boolean;
  permalink?: string;
}

export type InitialRecordSearch = {
  address: string;
  city: string;
  state: string;
  zip_code: string;
};

export default function PerksPage({
  filterByCategories = false,
  title = 'Member Perks',
  useSearch = false,
  last5RecentReports = [],
  isRecentReportsLoading = false,
  permalink = undefined,
}: PerksPageProps) {
  const [carwiserModalIsOpen, setCarwiserModalIsOpen] = useState(false);
  const [activeReward, setActiveReward] = useState<string | Reward>('');
  const { isMobile } = useScreenSize();
  const {
    data: { brandName, rewardsDisclaimer, rewardsDescription },
    images: { rewardsBannerMobile, rewardsBannerDesktop },
  } = useAppConstantsContext();
  const { data, isLoading } = useRewards();
  const hasSelectedReward = activeReward !== '';
  // The triple bang will avoid using this request if the permalink is not present
  // permalink is only present for brands that use report dat

  const { data: rawReportData } = useRewardsReport(permalink!, permalink);

  const isComponentLoading = isLoading || isRecentReportsLoading;
  const renderSearchProperty = !isComponentLoading && useSearch;

  const [selectedReport, setSelectedReport] = useState<
    serializedTypes.SerializedProperty | undefined
  >();
  const [initialRecordSearch, setInitialRecordSearch] =
    useState<InitialRecordSearch>({
      address: '',
      city: '',
      state: '',
      zip_code: '',
    });

  useEffect(() => {
    if (rawReportData) {
      if (
        rawReportData?.meta?.no_entities === false &&
        rawReportData?.entities?.properties.length > 0
      ) {
        const report = SerializeProperty(
          rawReportData?.entities?.properties[0]
        );
        setSelectedReport(report);
      }

      setInitialRecordSearch({
        address: rawReportData?.meta?.search_data?.address,
        city: rawReportData?.meta?.search_data?.city,
        state: rawReportData?.meta?.search_data?.state,
        zip_code: rawReportData?.meta?.search_data?.zip_code,
      });
    }
  }, [rawReportData]);

  return (
    <>
      {carwiserModalIsOpen && (
        <CarwiserModal setIsOpen={setCarwiserModalIsOpen} />
      )}
      <Stack gap={2}>
        <TextWithIcon variant="h1">
          <Icons.Redeem fontSize="large" /> {title}
        </TextWithIcon>
        <RewardsBanner
          src={isMobile ? rewardsBannerMobile : rewardsBannerDesktop}
          alt="Access thousands in rewards"
        />
        <Text fontSize="1.125rem" textAlign="center">
          {rewardsDescription || (
            <>
              As a {brandName} member, you can take advantage of benefits and
              perks that come with your subscription.
              <br />
              Check back often as we add more savings - we're just getting
              started!
            </>
          )}
        </Text>
        {renderSearchProperty && (
          <SearchPropertyContent
            permalink={permalink}
            isRecentReportsLoading={isRecentReportsLoading}
            last5RecentReports={last5RecentReports}
          />
        )}
        {isComponentLoading ? (
          <Text variant="h3">Loading...</Text>
        ) : (
          data?.rewards && (
            <Rewards
              rewards={data?.rewards}
              state={rawReportData?.meta?.search_data?.state}
              zipCode={rawReportData?.meta?.search_data?.zip_code}
              activeReward={activeReward}
              usesFilterByState={useSearch}
              hasSelectedReward={hasSelectedReward}
              selectRewardPartner={setActiveReward}
              filterByCategories={filterByCategories}
              setCarwiserModalIsOpen={setCarwiserModalIsOpen}
              report={selectedReport}
              initialRecordSearch={initialRecordSearch}
            />
          )
        )}
        <Text variant="caption" marginTop={1}>
          <strong>Advertising Disclaimer: </strong>
          {rewardsDisclaimer}
        </Text>
      </Stack>
      <Modal
        fullWidth
        hasCloseIcon
        sx={modalStyles}
        open={hasSelectedReward}
        onClose={() => setActiveReward('')}
      >
        {hasSelectedReward && (
          <ClickOutPartner
            report={selectedReport}
            reward={activeReward as Reward}
            currentRewardCollapsed={false}
            partnerName={(activeReward as Reward).name}
            selectRewardPartner={() => setActiveReward('')}
            initialRecordSearch={initialRecordSearch}
            isFormReward={
              (activeReward as Reward).component !== CLICK_OUT_PARTNER_COMPONENT
            }
          />
        )}
      </Modal>
    </>
  );
}
